/**
 * Navigation / Dead-Reckoning System Types
 *
 * Shared contract between the dashboard (the "brain": all game logic, rolls,
 * and the Dead-Reckoning Log UI) and the Owlbear Rodeo extension (the "hands":
 * creates and moves tokens on the hex map via the OBR SDK).
 *
 * FROZEN FIELD NAMES: `periodNumber`, `lostSincePeriod`, `recognizedPeriod` and
 * `arrivedPeriod` date from the old period-based seafaring system. The voyage
 * now advances one day at a time and these carry the voyage day, but the names
 * are duplicated by hand in `obr-extension/src/nav-types.ts` and read by the
 * deployed extension. Renaming them here breaks a live client — don't.
 *
 * Both halves reconcile toward the same synced state under
 * `sessions/{id}/gameState/navigation` and `.../navLog`, rather than exchanging
 * imperative commands — this survives reconnects and avoids queue race conditions.
 *
 * Models the Stormwrack "lost at sea" mechanic: a ship can be off course
 * (truth diverges) for days before the crew *recognizes* it (a daily
 * Knowledge (geography) check at DC 20, -1 per day of random travel). While
 * unaware, the player-visible "believed" token keeps sailing the plotted line
 * (the lie); the DM-only "truth" token drifts.
 *
 * SECURITY NOTE (prototype): all of `gameState` syncs to every client, so
 * `navigation.truth` and DM-only `navLog` rows are only *soft-hidden* via the
 * `isDMOnly` filter, exactly like DM-only clocks. Hardening path: move truth +
 * DM-only log rows to `sessions/{id}/dmState` with a Firebase rule granting read
 * only to the DM's uid. Not done yet — see design notes.
 */

/** Axial hex coordinates. Absolute geometry is seeded once by the OBR extension. */
export type HexCoord = { q: number; r: number };

/**
 * 0-5 hex-face directions. The compass bearing each index points depends on the
 * grid orientation — see NavGrid.direction0Bearing. Drift on a hex map uses
 * these six directions (not a d8 compass), though the log may render a compass
 * label for the DM.
 */
export type HexDirection = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * The navigation state machine. Transitions are driven by fields the dashboard
 * already tracks (voyageDestination.courseSettingResult, consecutiveFailures,
 * voyageDay).
 */
export type NavStatus =
  | 'not-charted'   // no course set yet
  | 'on-course'     // believed === truth, both advance together
  | 'lost-unaware'  // truth drifts; believed keeps sailing the plotted line (the lie)
  | 'lost-aware'    // players have realized; believed token hidden
  | 'arrived';      // truth reached a recognizable hex; believed snaps to truth

export type NavEventType =
  | 'chart-course'
  | 'advance'            // normal on-course move
  | 'became-lost'        // truth silently diverges (DM-only)
  | 'drift'              // one random hex move while lost (DM-only)
  | 'recognition-check'  // the daily DC 20 - days check
  | 'recognized'         // players realize they're lost
  | 'rechart'
  | 'arrived'
  | 'manual-adjust';     // DM dragged a token directly in OBR

/**
 * One row of the Dead-Reckoning Log. Mirrors ClockHistoryEntry conventions so
 * the DM-only filtering and rendering patterns carry over.
 */
export interface NavLogEntry {
  id: string;
  periodNumber: number;
  timestamp: number;
  eventType: NavEventType;

  /** Where the truth token moved to (DM-only entries carry this). */
  truthHex?: HexCoord;
  /** Where the players think they are. */
  believedHex?: HexCoord;

  direction?: HexDirection;
  /** Human-readable heading for the log, e.g. "NE". */
  compassLabel?: string;
  distanceHexes?: number;

  /** For recognition and course-setting checks. */
  check?: {
    skill: string; // 'Knowledge (Geography)'
    dc: number;
    roll: number;  // total result including modifiers
    success: boolean;
  };

  /** Days of random travel at the time of this entry (drives the recognition DC). */
  daysOfRandomTravel?: number;

  /** DM-facing description of what happened. */
  reason: string;

  /** Truth-bearing rows are DM-only (same pattern as isDMOnly clocks). */
  isDMOnly: boolean;
}

/** Grid descriptor, seeded once by the extension from the live OBR scene. */
export interface NavGrid {
  type: 'hex';
  orientation: 'pointy' | 'flat';
  /** Compass bearing (deg) that HexDirection 0 points — lets the log render "NE" etc. */
  direction0Bearing: number;
}

/** Base token binding: the OBR item id (set by the extension) and its hex. */
export interface NavToken {
  /** Set by the extension once it creates/binds the OBR item. */
  obrItemId?: string;
  hex: HexCoord;
}

/**
 * The player-visible token. `display` covers the recognition behaviour:
 * 'hidden' vanishes it (current choice); 'ghost' would grey it at the last
 * believed hex (supported by the schema but not used yet).
 */
export interface BelievedToken extends NavToken {
  display: 'normal' | 'ghost' | 'hidden';
}

export interface NavigationState {
  status: NavStatus;

  /** Seeded once by the extension; the dashboard does hex math after that. */
  grid?: NavGrid;

  /** Synced, player-visible. */
  believed: BelievedToken;
  /** DM-only ground truth. Soft-hidden in the prototype — see security note above. */
  truth: NavToken;

  /**
   * @deprecated Nothing reads this. It existed so the engine could walk the
   * believed token one hex down the plotted line each period; the crew now
   * sails that token by hand on the map, so there is no line to walk. Kept on
   * the type so existing session records still parse.
   */
  heading?: HexDirection;
  /** Destination hex, if known to the party. */
  destinationHex?: HexCoord;

  /**
   * Where the believed token sat when the last day was resolved. The crew moves
   * that token freely between days, so the distance from here to its current
   * hex is the day's run — which is how far the ship drifts when it is off
   * course. Seeded at chart time and rewritten every resolved day.
   */
  dayStartBelieved?: HexCoord;

  // Stormwrack recognition mechanic
  /** Accumulates timeScale.segmentDuration each lost period; recognition DC = 20 - this. */
  daysOfRandomTravel: number;
  lostSincePeriod?: number;
  recognizedPeriod?: number;
  arrivedPeriod?: number;

  // Concurrency guard against echo loops between brain and hands.
  // In each side's onValue handler, skip reconciliation when updatedBy === self.
  rev: number;
  updatedBy: 'dashboard' | 'extension';
}

/** Default navigation state for a new session / voyage reset. */
export const defaultNavigationState: NavigationState = {
  status: 'not-charted',
  believed: { hex: { q: 0, r: 0 }, display: 'normal' },
  truth: { hex: { q: 0, r: 0 } },
  daysOfRandomTravel: 0,
  rev: 0,
  updatedBy: 'dashboard',
};
